<template>
  <div
    class="vz-layout vz-layout-app"
    :class="{ 'vz-anonymous': isUserAnonymous }"
  >
    <vz-header class="vz-app-header" />
    <nuxt />
    <vz-footer class="vz-app-footer" :sections="footerSections" />

    <transition>
      <title-details
        class="vz-title-details"
        v-if="currentTitle"
        :title="currentTitle"
        :has-my-list="hasMyList"
        @close="hideTitleDetails"
        @play="playTitle"
        @account="gotoAccount"
        @change-plan="gotoAccountPlan"
        @complete-profile="gotoCompleteProfile"
        @rent="rentTitle"
        @title-details="showTitleDetails"
        @create-account="gotoSignUp"
        @fav-add="addTitleToFavorites"
        @fav-remove="removeTitleFromFavorites"
      >
      </title-details>
    </transition>

    <vz-dialog
      class="vz-app-dialog"
      :active="dialog.active"
      :title="dialog.title"
      :type="dialog.type"
      :buttons="dialog.buttons"
    >
      {{ dialog.text }}
    </vz-dialog>
    <vz-cookies />
    <terms-dialog />
  </div>
</template>

<script>
import VzDialog from 'vsp-web-ui-lib/components/organisms/vz-dialog.vue';
import Base from './_base.vue';
import VzHeader from '@/components/organisms/header.vue';
import VzFooter from '@/components/organisms/footer.vue';
import TitleDetails from '@/components/organisms/title-details.vue';
import VzCookies from '@/components/organisms/cookies-popup.vue';
import TermsDialog from '../components/atoms/terms-dialog.vue';

let listenersRegistered = true;

export default {
  name: 'AppLayout',
  extends: Base,
  emits: ['fav-added', 'fav-removed'],
  components: {
    TermsDialog,
    VzDialog,
    VzHeader,
    VzFooter,
    TitleDetails,
    VzCookies
  },

  middleware: ['mobile-app-check', 'auth'],

  computed: {
    dialog() {
      return this.$store.state.ui.dialog;
    },

    footerSections() {
      return this.$store.state.config?.content?.links || [];
    },

    currentTitle() {
      const title = this.$store.state.content.current;
      const uiActive = this.$store.state.ui.titleDetailsActive;
      return title.info.id && uiActive ? title : null;
    },

    isUserAnonymous() {
      return this.$store.state.user.user.anonymous;
    },

    hasMyList() {
      const menusUser = this.$store.state.ui.navigation?.user || [];
      const hasMyList = menusUser.some((menu) => menu.type === 'MY_LIST');
      return hasMyList;
    }
  },

  fetch() {
    return this.$store.dispatch('ui/getNavigation');
  },

  created() {
    const listener = listenersRegistered;
    if (listener) {
      this.$nuxt.$off('title-details', this.showTitleDetails);
      this.$nuxt.$on('title-details', this.showTitleDetails);

      this.$nuxt.$off('hide-details', this.hideTitleDetails);
      this.$nuxt.$on('hide-details', this.hideTitleDetails);

      this.$nuxt.$off('play', this.playTitle);
      this.$nuxt.$on('play', this.playTitle);

      this.$nuxt.$off('fav-add', this.addTitleToFavorites);
      this.$nuxt.$on('fav-add', this.addTitleToFavorites);

      this.$nuxt.$off('fav-remove', this.removeTitleFromFavorites);
      this.$nuxt.$on('fav-remove', this.removeTitleFromFavorites);

      this.$nuxt.$off('rent', this.rentTitle);
      this.$nuxt.$on('rent', this.rentTitle);

      this.$nuxt.$off('group', this.gotoGroup);
      this.$nuxt.$on('group', this.gotoGroup);

      listenersRegistered = false;
    }
  },

  watch: {
    $route() {
      // Hide title details when changing route.
      this.hideTitleDetails();
    }
  },

  methods: {
    isCurrentTitle(title) {
      return title?.id === this.$store.state.content.current.info.id;
    },

    hideTitleDetails() {
      this.$store.commit('ui/setTitleDetailsActive', false);
    },

    showTitleDetails(title) {
      const store = this.$store;
      const next = this.$store.state.ui.titleDetailsActive;
      if (next) {
        store.commit('content/setNextInfo', title);
        store.commit('content/setNextStatus', 'ready');
        this.hideTitleDetails();
        setTimeout(() => {
          store.commit('content/promoteNextToCurrent');
          store.commit('ui/setTitleDetailsActive', true);
        }, 400);
      } else {
        store.commit('content/setCurrentInfo', title);
        store.commit('content/setCurrentStatus', 'ready');
        store.commit('ui/setTitleDetailsActive', true);
      }
    },

    playTitle(title) {
      if (
        title.specifics.contentType === 'MODULE' ||
        title.specifics.contentType === 'COURSE'
      ) {
        this.$store.dispatch('content/selectContent', title);
        this.showTitleDetails(title);
        return;
      }

      this.$store.dispatch('content/setContentToPlayer', title);
      this.$router.push(`/view/${title.id}`);
    },

    showFavoritesError(error, title, errorOnRemove) {
      const defaultMsg = errorOnRemove
        ? `Não foi possível remover "${title.title}" da sua lista. Tente novamente mais tarde.`
        : `Não foi possível adicionar "${title.title}" na sua lista. Tente novamente mais tarde.`;
      this.$store.dispatch('ui/showError', {
        text: error.message || error || defaultMsg,
        callback: () => {
          if (error.status >= 400 && error.status < 500) {
            this.$router.push({
              path: `/home`,
              force: true
            });
          }
        }
      });
    },

    requestAddToFavorites(title) {
      return this.$api.content
        .addFavorite(this.$store.state.user.session.token, title.id)
        .then((response) => {
          return response?.data
            ? Promise.resolve(response.data)
            : Promise.reject(response);
        });
    },

    requestRemoveFromFavorites(title) {
      return this.$api.content
        .deleteFavorite(this.$store.state.user.session.token, title.id)
        .then((response) => {
          return response?.data
            ? Promise.resolve(response.data)
            : Promise.reject(response);
        });
    },

    getFunctionsForFavorites(title) {
      let titleObj;
      let changeFav;
      if (this.isCurrentTitle(title)) {
        titleObj = this.$store.state.content.current.info;
        changeFav = (value) => {
          this.$store.commit('content/setTitleFavorite', {
            next: false,
            value
          });
        };
      } else {
        titleObj = title;
        changeFav = (value) => {
          title.inFavorites = value;
        };
      }
      return { titleObj, changeFav };
    },

    addTitleToFavorites(title) {
      console.trace('Stack');
      const { titleObj, changeFav } = this.getFunctionsForFavorites(title);
      changeFav(true);
      this.requestAddToFavorites(titleObj)
        .then(() => {
          this.$nuxt.$emit('fav-added', title);
        })
        .catch((err) => {
          changeFav(false);
          this.showFavoritesError(err, titleObj, false);
        });
    },

    removeTitleFromFavorites(title) {
      const { titleObj, changeFav } = this.getFunctionsForFavorites(title);
      changeFav(false);
      this.requestRemoveFromFavorites(titleObj)
        .then(() => {
          this.$nuxt.$emit('fav-removed', titleObj);
        })
        .catch((err) => {
          changeFav(true);
          this.showFavoritesError(err, titleObj, true);
        });
    },

    showRentError(error, title) {
      this.$store.dispatch('ui/showAlert', {
        text:
          error.message ||
          error ||
          `Não foi possível alugar "${title.title}". Tente novamente mais tarde.`
      });
    },

    requestRentTitle(title) {
      return this.$api.content
        .acquireContent(
          this.$store.state.user.session.token,
          this.$store.state.user.session.deviceManagementId,
          title.id,
          title?.availability?.rent || 0,
          this.$route.path
        )
        .then((response) => {
          if (response?.data?.status) {
            return response.data.status === 'CANCELED'
              ? Promise.reject(
                  `Não foi possível alugar "${title.title}". Tente novamente mais tarde.`
                )
              : Promise.resolve(response.data.status);
          }
          return Promise.reject(response);
        })
        .then(() => {
          return this.$api.content.getTitleDetails(
            this.$store.state.config.distributor.id,
            this.$store.state.user.session.token,
            title.id
          );
        })
        .then((response) => {
          return response.data;
        });
    },

    rentTitle(title, statusObj) {
      let titleObj;
      let changeStatus;
      let changeAccess;
      if (this.isCurrentTitle(title)) {
        titleObj = this.$store.state.content.current.info;
        changeStatus = (value) => {
          this.$store.commit('content/setCurrentStatus', value);
        };
        changeAccess = (value) => {
          this.$store.commit('content/setCurrentInfo', { access: value });
        };
      } else {
        titleObj = title;
        changeStatus = (value) => {
          if (statusObj) statusObj.status = value;
        };
        changeAccess = (value) => {
          titleObj.access = value;
        };
      }
      if (statusObj?.status !== 'renting') {
        changeStatus('renting');
        this.requestRentTitle(titleObj)
          .then((titleDetails) => {
            changeAccess(titleDetails.access);
            return true;
          })
          .catch((err) => {
            this.showRentError(err, titleObj);
            return false;
          })
          .finally(() => {
            changeStatus('ready');
          });
      }
    },

    gotoAccount() {
      this.$router.push('/my-account');
    },
    gotoAccountPlan(data) {
      const { plans } = data.availability;
      if (plans) {
        this.$router.push(`/my-account/plan?highlight=${plans}`);
      } else {
        this.$router.push(`/my-account/plan`);
      }
    },
    gotoCompleteProfile() {
      this.$router.push('/sign-up/complete-profile');
    },
    gotoSignUp() {
      this.$router.push('/sign-up');
    },

    gotoGroup(groupInfo) {
      let route;
      if (groupInfo.link) {
        route = groupInfo.link;
      } else if (groupInfo.id) {
        route = `/channel/${groupInfo.id}`;
      } else {
        route = '/home';
      }
      this.$router.push(route);
    }
  }
};
</script>

<style>
@import '~theme/style.css';
@import '~client/style.css';
@import '@fortawesome/fontawesome-free/css/all.css';

.vz-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.vz-app-footer {
  margin-top: auto;
}
</style>

<style theme="dark-theater" scoped>
.vz-app-header {
  z-index: var(--header-index);
}

.vz-title-details {
  z-index: var(--modal-overlay-index);
}

.vz-app-dialog {
  z-index: var(--dialog-index);
}
</style>














